// src/utils/permissions.js

// ─── Legacy role-level helpers (kept for backward compatibility) ────────────────
export const ROLE_LEVEL = {
  director: 4,
  admin: 4,
  dvaho: 3,
  pvc: 2,
  vas: 2,
  scah: 1
};

export const ROLE_NAMES = {
  director: 'Director',
  admin: 'Admin',
  dvaho: 'DVAHO',
  pvc: 'PVC/VAS',
  vas: 'VAS',
  scah: 'SC(AH)'
};

export const canAccess = (userRole, requiredRole) => {
  return (ROLE_LEVEL[userRole] || 0) >= (ROLE_LEVEL[requiredRole] || 0);
};

export const canManage = (userRole, targetRole) => {
  return canAccess(userRole, targetRole);
};

export const ROLE_HIERARCHY = [
  { role: 'director', level: 4, label: 'Director' },
  { role: 'admin', level: 4, label: 'Admin' },
  { role: 'dvaho', level: 3, label: 'DVAHO' },
  { role: 'pvc', level: 2, label: 'PVC/VAS' },
  { role: 'vas', level: 2, label: 'VAS' },
  { role: 'scah', level: 1, label: 'SC(AH)' }
];

// ─── Roles that bypass all permission checks (super-admin) ────────────────────
const SUPER_ADMIN_ROLES = ['director', 'admin'];

/**
 * Returns true if the given role bypasses all permission checks.
 * @param {string|undefined} role
 */
export function isSuperAdmin(role) {
  return SUPER_ADMIN_ROLES.includes(role);
}

// ─── Key/action permission helpers ────────────────────────────────────────────

/**
 * Check a single permission flag from the user.permissions object.
 *
 * @param {object|null|undefined} permissions  - user.permissions from the auth store
 * @param {string} moduleKey                   - e.g. 'hrms_employees'
 * @param {string} [action='view']             - e.g. 'add', 'edit', 'delete', 'view'
 * @returns {boolean}
 */
export function hasPermission(permissions, moduleKey, action = 'view') {
  if (!permissions || !moduleKey) return false;
  const mod = permissions[moduleKey];
  if (!mod || typeof mod !== 'object') return false;
  return mod[action] === true;
}

/**
 * Returns true if the user has at least one action enabled on a module.
 *
 * @param {object|null|undefined} permissions
 * @param {string} moduleKey
 * @returns {boolean}
 */
export function hasAnyPermission(permissions, moduleKey) {
  if (!permissions || !moduleKey) return false;
  const mod = permissions[moduleKey];
  if (!mod || typeof mod !== 'object') return false;
  return Object.values(mod).some(Boolean);
}

/**
 * Convenience: check if user can VIEW a module.
 *
 * @param {object|null|undefined} permissions
 * @param {string} moduleKey
 * @returns {boolean}
 */
export function canViewModule(permissions, moduleKey) {
  return hasPermission(permissions, moduleKey, 'view');
}

/**
 * Returns an object with boolean flags for all standard CRUD actions.
 *
 * @param {object|null|undefined} permissions
 * @param {string} moduleKey
 * @param {string|undefined} role - if super-admin role, all flags are true
 * @returns {{ canView: boolean, canAdd: boolean, canEdit: boolean, canDelete: boolean }}
 */
export function getModulePermissions(permissions, moduleKey, role) {
  if (isSuperAdmin(role)) {
    return { canView: true, canAdd: true, canEdit: true, canDelete: true };
  }
  return {
    canView:   hasPermission(permissions, moduleKey, 'view'),
    canAdd:    hasPermission(permissions, moduleKey, 'add'),
    canEdit:   hasPermission(permissions, moduleKey, 'edit'),
    canDelete: hasPermission(permissions, moduleKey, 'delete'),
  };
}